(() => {
  function initialize(root = document) {
    root.querySelectorAll('[data-brand-directory]').forEach(directory => {
      const input = directory.querySelector('[data-brand-query]');
      if (!input || input.dataset.ready) return;
      input.dataset.ready = 'true';
      const cards = [...directory.querySelectorAll('[data-brand-name]')];
      const counter = directory.querySelector('[data-brand-count]');
      const originalCount = counter.textContent;
      const filter = () => {
        const query = input.value.trim().toLocaleLowerCase();
        let count = 0;
        cards.forEach(card => {
          card.hidden = !card.dataset.brandName.toLocaleLowerCase().includes(query);
          if (!card.hidden) count++;
        });
        counter.textContent = query ? `${count} / ${originalCount}` : originalCount;
        directory.querySelector('[data-brand-empty]').hidden = count > 0;
      };
      input.addEventListener('input', filter);
      directory.querySelector('[data-brand-reset]').addEventListener('click', () => {
        input.value = '';
        filter();
        input.focus();
      });
    });
    root.querySelectorAll('[data-filter-panel]').forEach(panel => { panel.hidden = true; });
    root.querySelectorAll('[data-filter-toggle]').forEach(toggle => { toggle.hidden = false; });
  }

  document.addEventListener('click', event => {
    const toggle = event.target.closest('[data-filter-toggle]');
    if (toggle) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      panel.hidden = !panel.hidden;
      toggle.setAttribute('aria-expanded', String(!panel.hidden));
    }
    document.querySelectorAll('[data-navigation][open], [data-header-menu][open], [data-catalog-popover][open]').forEach(menu => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-navigation][open], [data-header-menu][open], [data-catalog-popover][open]').forEach(menu => {
      menu.open = false;
      menu.querySelector('summary').focus();
    });
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-sort], [data-catalog-auto-submit]')) event.target.form.requestSubmit();
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));

  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.fields = [...this.querySelectorAll('[data-price-value]')];
      this.handles = [...this.querySelectorAll('[data-price-handle]')];
      if (this.fields.length !== 2 || this.handles.length !== 2) return;
      // Retain explicit URL bounds even when Shopify returns a narrower result range.
      this.ceiling = Math.max(0, Number(this.dataset.max) || 0, ...this.fields.map(field => field.valueAsNumber || 0));
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency });
      this.handles.forEach(handle => {
        handle.max = this.ceiling;
        handle.disabled = this.ceiling === 0;
      });
      this.fields.forEach(field => { field.max = this.ceiling; });
      this.addEventListener('input', event => {
        const index = this.handles.indexOf(event.target);
        if (index >= 0) {
          this.fields[index].value = event.target.value;
          this.update(index, true);
          if (Number(this.fields[index].value) === (index ? this.ceiling : 0)) this.fields[index].value = '';
        } else if (this.fields.includes(event.target)) this.update(this.fields.indexOf(event.target));
      }, options);
      this.addEventListener('change', event => {
        if (this.fields.includes(event.target)) this.update(this.fields.indexOf(event.target), true);
      }, options);
      this.closest('form')?.addEventListener('reset', () => {
        requestAnimationFrame(() => { if (this.isConnected) this.update(); });
      }, options);
      this.update();
      this.querySelector('.price-range__track').hidden = false;
    }

    update(changed = 0, commit = false) {
      const values = this.fields.map((field, index) => Number.isFinite(field.valueAsNumber) ? Math.max(0, Math.min(this.ceiling, field.valueAsNumber)) : (index ? this.ceiling : 0));
      if (changed === 0) values[0] = Math.min(values[0], values[1]);
      else values[1] = Math.max(values[0], values[1]);
      values.forEach((value, index) => {
        this.handles[index].value = value;
        this.handles[index].setAttribute('aria-valuetext', this.money.format(value));
        if (commit && this.fields[index].value !== '') this.fields[index].value = Number(value.toFixed(2));
      });
      this.handles[0].setAttribute('aria-valuemax', values[1]);
      this.handles[1].setAttribute('aria-valuemin', values[0]);
      const low = this.ceiling ? values[0] / this.ceiling * 100 : 0;
      const high = this.ceiling ? values[1] / this.ceiling * 100 : 0;
      this.style.setProperty('--range-low', low + '%');
      this.style.setProperty('--range-high', high + '%');
      // Split the native thumb hit areas so both handles remain reachable when they meet.
      const midpoint = (low + high) / 2;
      this.style.setProperty('--range-split', `calc(${midpoint}% + ${14 - 28 * midpoint / 100}px)`);
    }

    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

  class StoreRail extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      const track = this.querySelector('[data-rail-track]');
      const controls = this.querySelector('[data-rail-controls]');
      if (!track || !controls) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      let drag = null;
      let suppressClick = false;
      const update = () => {
        const limit = track.scrollWidth - track.clientWidth;
        controls.hidden = limit < 2;
        controls.querySelector('[data-rail-direction="-1"]').disabled = track.scrollLeft < 2;
        controls.querySelector('[data-rail-direction="1"]').disabled = track.scrollLeft >= limit - 2;
      };
      controls.addEventListener('click', event => {
        const button = event.target.closest('[data-rail-direction]');
        if (!button) return;
        track.scrollBy({ left: track.clientWidth * Number(button.dataset.railDirection), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      track.addEventListener('scroll', update, { ...options, passive: true });
      track.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      }, options);
      track.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          track.setPointerCapture(event.pointerId);
          this.classList.add('is-dragging');
        }
        track.scrollLeft = drag.left - distance;
      }, options);
      const endDrag = event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (drag.moved) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 0);
        }
        drag = null;
        this.classList.remove('is-dragging');
      };
      track.addEventListener('pointerup', endDrag, options);
      track.addEventListener('pointercancel', endDrag, options);
      track.addEventListener('dragstart', event => event.preventDefault(), options);
      track.addEventListener('click', event => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, { ...options, capture: true });
      this.observer?.disconnect();
      this.observer = new ResizeObserver(update);
      this.observer.observe(track);
      [...track.children].forEach(child => this.observer.observe(child));
      update();
    }

    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  if (!customElements.get('store-rail')) customElements.define('store-rail', StoreRail);

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      const navigation = this.querySelector('[data-collection-tabs]');
      this.tabs = [...this.querySelectorAll('[data-tab-target]')];
      if (!navigation || !this.tabs.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.classList.add('is-enhanced');
      navigation.hidden = false;
      navigation.setAttribute('role', 'tablist');
      this.tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.tabTarget);
        const panel = document.getElementById(tab.dataset.tabTarget);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
      });
      this.select(this.tabs[0]);
      navigation.addEventListener('click', event => {
        const tab = event.target.closest('[data-tab-target]');
        if (tab) this.select(tab);
      }, options);
      navigation.addEventListener('keydown', event => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[next]);
        this.tabs[next].focus();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-collection-panel]');
        const tab = this.tabs.find(item => item.dataset.tabTarget === panel?.id);
        if (tab) this.select(tab);
      }, options);
    }

    select(selected) {
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        document.getElementById(tab.dataset.tabTarget).hidden = !active;
      });
    }

    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('collection-tabs')) customElements.define('collection-tabs', CollectionTabs);

  class CampaignSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-campaign-track]');
      this.slides = [...this.querySelectorAll('[data-campaign-slide]')];
      const controls = this.querySelector('[data-campaign-controls]');
      if (!controls || this.slides.length < 2) return;
      controls.hidden = false;
      this.index = 0;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-campaign-direction]');
        if (button) this.goTo(this.index + Number(button.dataset.campaignDirection));
      });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const trackLeft = this.track.getBoundingClientRect().left;
          this.index = this.slides.reduce((closest, slide, index) => Math.abs(slide.getBoundingClientRect().left - trackLeft) < Math.abs(this.slides[closest].getBoundingClientRect().left - trackLeft) ? index : closest, 0);
          this.querySelector('[data-campaign-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
        });
      }, { passive: true });
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.editorSelect = event => {
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) this.goTo(index);
      };
      this.addEventListener('shopify:block:select', this.editorSelect);
    }

    disconnectedCallback() { cancelAnimationFrame(this.scrollFrame); }

    goTo(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides[this.index].scrollIntoView({ block: 'nearest', inline: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  }
  if (!customElements.get('campaign-slideshow')) customElements.define('campaign-slideshow', CampaignSlideshow);

  class ProductDetail extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelectorAll('.product-description, .product-summary, .product-intro').forEach(content => window.SupplierContent?.clean(content));
      this.prepareQuickShop();
      this.populateBenefits();
      this.structureDescription();
      this.normalizeDescription();
      this.activateFeaturedMedia();
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('submit', event => this.onSubmit(event));
      this.addEventListener('scroll', event => this.onGalleryScroll(event), true);
    }

    disconnectedCallback() { this.controller?.abort(); }

    prepareQuickShop() {
      if (this.dataset.presentation !== 'quick-shop') return;
      this.classList.remove('product-detail--editorial', 'full-width');
      const intro = this.querySelector('.product-intro:not(.rte)');
      const paragraph = this.querySelector('.product-description p');
      if (intro && paragraph?.textContent.trim()) intro.textContent = paragraph.textContent.trim();
      this.querySelectorAll(':scope > nav, .product-information, .product-benefits, [data-product-details-link], .product-native-checkout, .product-payment-terms, .product-sku, .product-summary, .product-delivery, .product-policy').forEach(node => node.remove());
      const title = this.querySelector('.product-info h1');
      if (title) {
        const heading = document.createElement('h2');
        heading.className = 'product-info__title';
        heading.id = 'quick-shop-title';
        heading.textContent = title.textContent;
        title.replaceWith(heading);
      }
      const gallery = this.querySelector('[data-gallery]');
      const stage = gallery?.querySelector('.product-gallery__stage');
      if (stage) {
        gallery.classList.remove('product-gallery--stacked');
        stage.removeAttribute('tabindex');
        const items = [...stage.querySelectorAll('[data-media-id]')];
        const active = items.find(item => item.dataset.mediaId === stage.dataset.featuredMedia) || items[0];
        items.forEach(item => { item.hidden = item !== active; });
        if (active) this.updateGalleryThumbnails(gallery, active.dataset.mediaId);
      }
      // A quick shop can open on a product page with the same section and form IDs.
      const ids = new Map();
      this.querySelectorAll('[id]').forEach(node => {
        const oldId = node.getAttribute('id');
        const newId = oldId.startsWith('quick-shop-') ? oldId : 'quick-shop-' + oldId;
        node.setAttribute('id', newId);
        ids.set(oldId, newId);
      });
      this.querySelectorAll('[for], [aria-controls], [aria-labelledby], [aria-describedby]').forEach(node => {
        for (const name of ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
          if (node.hasAttribute(name)) node.setAttribute(name, node.getAttribute(name).split(/\s+/).map(id => ids.get(id) || id).join(' '));
        }
      });
    }

    populateBenefits() {
      const card = this.querySelector('[data-product-benefits][data-benefits-source="description"]');
      const description = this.querySelector('.product-information .product-description');
      if (!card || !description) return;

      const label = card.querySelector('h2')?.textContent.trim().toLocaleLowerCase();
      const clean = value => value.trim().toLocaleLowerCase().replace(/[\s:：.]+$/u, '');
      const headingNames = [label, 'benefits', 'hyödyt', 'edut', 'fördelar', 'fordele', 'fordeler'];
      const signal = /\b(helps?|supports?|contributes?|provid\w*|offers?|deliver\w*|maintain\w*|keeps?|moisturi[sz]\w*|hydrat\w*|nourish\w*|sooth\w*|protect\w*|brighten\w*|soften\w*|improv\w*|reduc\w*|promot\w*|restor\w*|cleans\w*|exfoliat\w*|replenish\w*)\b|auttaa|tukee|edistää|tarjoaa|kosteuttaa|rauhoittaa|suojaa|pehmentää|vähentää|hjälper|stödjer|bidrar|ger|återfuktar|lugnar|skyddar|mjukgör|minskar|hjælper|støtter|bidrager|giver|fugter|beroliger|beskytter|blødgør|reducerer|hjelper|gir|fukter|mykgjør|reduserer/iu;
      const heading = [...description.querySelectorAll('h2, h3, h4, p > strong')]
        .find(node => headingNames.some(name => clean(node.textContent).endsWith(name)));
      const list = heading?.closest('p')?.nextElementSibling || heading?.nextElementSibling;
      let benefits = list?.matches('ul, ol')
        ? [...list.children].filter(item => item.matches('li')).slice(0, 4).map(item => {
            const lead = item.querySelector('strong');
            return lead && signal.test(lead.textContent) && item.textContent.trim().startsWith(lead.textContent.trim())
              ? lead.textContent.trim().replace(/[:：]\s*$/u, '')
              : item.textContent.trim();
          })
        : [];

      if (!benefits.length && 'Segmenter' in Intl) {
        const safetyHeading = /warning|caution|dosage|directions|usage|how to use|ingredients|nutrition|instructions|storage|safety|varoit|annostus|käyttö|ainesos|ravinto|säily|varning|dosering|använd|ingredien|näring|förvaring|advarsel|anvend|næring|opbevaring|bruk|oppbevaring/iu;
        const reject = /\b(do not|not recommended|keep out of reach|shipping|delivery|returns|demonstration store)\b|^(take|use|mix|store|keep|apply|consume)\b|ei suositella|säilytä|förvara|opbevar|oppbevar/iu;
        const paragraphs = [];
        let inSafetySection = false;
        for (const node of description.querySelectorAll('h2, h3, h4, p, div, span, pre')) {
          if (node.matches('h2, h3, h4')) {
            inSafetySection = safetyHeading.test(node.textContent);
          } else if (node.querySelector(':scope > strong') && safetyHeading.test(node.querySelector(':scope > strong').textContent)) {
            inSafetySection = true;
          } else if (!inSafetySection && (node.matches('p') || node.matches('div, pre') && !node.querySelector('p, div, pre') || node.matches('span') && node.parentElement === description)) {
            paragraphs.push(node);
          }
        }
        const segmenter = new Intl.Segmenter(document.documentElement.lang || 'en', { granularity: 'sentence' });
        const sentences = paragraphs.slice(0, 12)
          .flatMap(paragraph => [...segmenter.segment(paragraph.textContent)].map(part => part.segment.trim()))
          .filter(sentence => sentence.length >= 20 && sentence.length <= 240 && !reject.test(sentence));
        benefits = sentences.filter(sentence => signal.test(sentence)).slice(0, 2);
        if (!benefits.length) {
          const highlight = sentences.find(sentence => !/^(discover|explore|introducing|unleash|welcome|shop now)\b/iu.test(sentence));
          if (highlight) {
            benefits = [highlight];
            card.querySelector('h2').textContent = card.dataset.highlightsLabel;
          }
        }
        card.dataset.benefitsMode = 'sentences';
      } else {
        card.dataset.benefitsMode = 'list';
      }

      if (!benefits.length) return;
      const target = card.querySelector('[data-product-benefits-list]');
      target.replaceChildren(...benefits.map(benefit => {
        const item = document.createElement('li');
        item.textContent = benefit;
        return item;
      }));
      card.hidden = false;
    }

    normalizeDescription() {
      // Older supplier descriptions contain page headings inside their rich text.
      this.querySelectorAll('.product-description h1').forEach(heading => {
        const replacement = document.createElement('h2');
        for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
        replacement.append(...heading.childNodes);
        heading.replaceWith(replacement);
      });
    }

    structureDescription() {
      const facts = this.querySelector('.product-information__facts');
      const description = facts?.querySelector('.product-description');
      const disclosure = description?.closest('.product-disclosure--description');
      if (!disclosure || facts.querySelector('[data-content-field]:not([data-content-field="description"])')) return;

      const sections = [];
      let current = null;
      for (const node of [...description.childNodes]) {
        if (node.nodeType === Node.ELEMENT_NODE && /^H[23]$/.test(node.tagName) && node.textContent.trim()) {
          current = { title: node.textContent.trim(), nodes: [] };
          sections.push(current);
          node.remove();
        } else if (current) {
          current.nodes.push(node);
        }
      }
      if (!sections.length) return;

      const icon = disclosure.querySelector('summary .icon');
      let previous = disclosure;
      for (const section of sections) {
        if (!section.nodes.some(node => node.textContent?.trim() || node.nodeType === Node.ELEMENT_NODE && (node.matches?.('img, table, video, iframe') || node.querySelector?.('img, table, video, iframe')))) continue;
        const detail = document.createElement('details');
        detail.className = 'product-disclosure';
        detail.dataset.contentField = 'description-section';
        const summary = document.createElement('summary');
        summary.append(section.title);
        if (icon) summary.append(icon.cloneNode(true));
        const body = document.createElement('div');
        body.className = 'rte product-description';
        body.append(...section.nodes);
        detail.append(summary, body);
        previous.after(detail);
        previous = detail;
      }
      if (!description.textContent.trim() && !description.querySelector('img, table, video, iframe')) disclosure.remove();
    }

    onClick(event) {
      const detailsLink = event.target.closest('[data-product-details-link]');
      if (detailsLink && this.contains(detailsLink)) {
        const disclosure = this.querySelector('.product-information .product-disclosure--description');
        if (disclosure) {
          disclosure.open = true;
          requestAnimationFrame(() => disclosure.querySelector('summary')?.focus({ preventScroll: true }));
        }
        return;
      }
      const option = event.target.closest('[data-option-link]');
      if (option && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        if (!this.cartPending) this.selectOption(option);
        return;
      }
      const zoomTrigger = event.target.closest('[data-gallery-open]');
      if (zoomTrigger) {
        const gallery = zoomTrigger.closest('[data-gallery]');
        const media = zoomTrigger.closest('[data-gallery-full-image]');
        const images = [...gallery.querySelectorAll('[data-gallery-full-image]')];
        const dialog = gallery.querySelector('[data-gallery-dialog]');
        if (media && dialog?.showModal) {
          gallery.dataset.zoomIndex = images.indexOf(media);
          this.updateGalleryZoom(gallery);
          dialog.showModal();
        }
        return;
      }
      const zoomClose = event.target.closest('[data-gallery-close]');
      if (zoomClose) {
        zoomClose.closest('dialog').close();
        return;
      }
      const zoomStep = event.target.closest('[data-gallery-step]');
      if (zoomStep) {
        const gallery = zoomStep.closest('[data-gallery]');
        const images = gallery.querySelectorAll('[data-gallery-full-image]');
        gallery.dataset.zoomIndex = (Number(gallery.dataset.zoomIndex) + Number(zoomStep.dataset.galleryStep) + images.length) % images.length;
        this.updateGalleryZoom(gallery);
        return;
      }
      if (event.target.matches('dialog[data-gallery-dialog]')) {
        event.target.close();
        return;
      }
      const thumbnail = event.target.closest('[data-media-target]');
      if (thumbnail) {
        event.preventDefault();
        const gallery = thumbnail.closest('[data-gallery]');
        const stage = gallery?.querySelector('.product-gallery__stage');
        const aspectRatio = Number(thumbnail.dataset.mediaAspectRatio);
        if (stage && Number.isFinite(aspectRatio) && aspectRatio > 0) {
          stage.style.setProperty('--active-media-aspect-ratio', aspectRatio);
        }
        if (gallery?.classList.contains('product-gallery--stacked')) {
          const media = [...stage.querySelectorAll('[data-media-id]')].find(item => item.dataset.mediaId === thumbnail.dataset.mediaTarget);
          if (media) stage.scrollTo({ left: media.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        } else {
          gallery.querySelectorAll('[data-media-id]').forEach(media => {
            media.hidden = media.dataset.mediaId !== thumbnail.dataset.mediaTarget;
            if (!media.hidden) return;
            media.querySelectorAll('video').forEach(video => video.pause());
            media.querySelectorAll('iframe').forEach(frame => {
              // External players have different APIs; resetting their source stops playback.
              frame.src = frame.src;
            });
          });
        }
        this.updateGalleryThumbnails(gallery, thumbnail.dataset.mediaTarget);
      }
      const stepper = event.target.closest('[data-quantity-step]');
      if (stepper) {
        const input = stepper.parentElement.querySelector('input');
        Number(stepper.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateGalleryZoom(gallery) {
      const images = [...gallery.querySelectorAll('[data-gallery-full-image]')];
      const media = images[Number(gallery.dataset.zoomIndex)];
      const image = gallery.querySelector('[data-gallery-zoom-image]');
      if (!media || !image) return;
      image.src = media.dataset.galleryFullImage;
      image.alt = media.querySelector('img')?.alt || '';
      gallery.querySelectorAll('[data-gallery-step]').forEach(button => { button.hidden = images.length < 2; });
    }

    activateFeaturedMedia() {
      const gallery = this.querySelector('.product-gallery--stacked');
      const stage = gallery?.querySelector('.product-gallery__stage');
      if (!stage || !matchMedia('(max-width: 900px)').matches) return;
      const media = [...stage.querySelectorAll('[data-media-id]')].find(item => item.dataset.mediaId === stage.dataset.featuredMedia);
      if (media) stage.scrollLeft = media.offsetLeft;
    }

    updateGalleryThumbnails(gallery, mediaId) {
      gallery.querySelectorAll('[data-media-target]').forEach(item => {
        if (item.dataset.mediaTarget === mediaId) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
    }

    onGalleryScroll(event) {
      const stage = event.target;
      if (!stage.matches?.('.product-gallery--stacked .product-gallery__stage')) return;
      const items = stage.querySelectorAll('[data-media-id]');
      const index = Math.min(items.length - 1, Math.round(stage.scrollLeft / stage.clientWidth));
      if (items[index]) this.updateGalleryThumbnails(stage.closest('[data-gallery]'), items[index].dataset.mediaId);
    }

    async selectOption(option) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const url = new URL(option.href);
      const current = new URL(location.href);
      if (current.searchParams.has('view')) url.searchParams.set('view', current.searchParams.get('view'));
      // Merge rapid changes against pending choices, not the last rendered selection.
      if (this.pendingOptions && url.pathname === this.pendingPath) {
        this.pendingOptions[Number(option.dataset.optionPosition) - 1] = option.dataset.optionId;
        url.searchParams.set('option_values', this.pendingOptions.join(','));
      } else {
        this.pendingOptions = url.searchParams.get('option_values').split(',');
        this.pendingPath = url.pathname;
      }
      const pageUrl = new URL(url);
      url.searchParams.set('section_id', this.dataset.renderSection || this.dataset.sectionId);
      const submit = this.querySelector('[name="add"]');
      const previousQuantity = this.querySelector('[name="quantity"]').value;
      submit.disabled = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Variant request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector('product-detail');
        if (!replacement) throw new Error('Product section missing');
        if (controller.signal.aborted) return;
        this.innerHTML = replacement.innerHTML;
        this.prepareQuickShop();
        this.populateBenefits();
        this.structureDescription();
        this.normalizeDescription();
        this.activateFeaturedMedia();
        const quantity = this.querySelector('[name="quantity"]');
        quantity.value = previousQuantity;
        if (!quantity.checkValidity()) quantity.value = quantity.min || '1';
        this.pendingOptions = null;
        if (this.dataset.updateUrl !== 'false') history.replaceState({}, '', pageUrl);
        this.dispatchEvent(new CustomEvent('product:updated', { bubbles: true, detail: { url: pageUrl.href } }));
        this.querySelector(`[data-option-id="${CSS.escape(option.dataset.optionId)}"]`)?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.pendingOptions = null;
          this.querySelector('[data-form-status]').textContent = this.dataset.error;
          submit.disabled = this.querySelector('input[name="id"]').dataset.available !== 'true';
        }
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      if (event.submitter && event.submitter.name !== 'add') return;
      event.preventDefault();
      if (this.cartPending || this.hasAttribute('aria-busy')) return;
      if (!form.reportValidity()) return;
      this.cartPending = true;
      const button = event.submitter || form.querySelector('[name="add"]');
      const status = form.querySelector('[data-form-status]');
      const link = form.querySelector('[data-cart-link]');
      const root = window.Shopify?.routes?.root || '/';
      button.disabled = true;
      status.textContent = status.dataset.adding;
      link.hidden = true;
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || status.dataset.error);
        status.textContent = status.dataset.added;
        link.hidden = false;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { open: true } }));
      } catch (error) {
        status.textContent = error.message || status.dataset.error;
      } finally {
        button.disabled = false;
        this.cartPending = false;
      }
    }
  }
  if (!customElements.get('product-detail')) customElements.define('product-detail', ProductDetail);
  initialize();
})();
